import React, { useEffect, useRef, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { DateSelector } from "../../components/DateSelector/DateSelector";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { ArrowLeft, Search } from "lucide-react";
import images from "../../assets/images";
import { Link, useLocation } from "react-router-dom";
import { useTrainingStore, usePremisesStore } from "../../store/store";
import { useAuth } from "../../auth/AuthProvider";
import { DotLoader, SpinLoader } from "../../components/loader/Loader";
import { Training, Test, Trainee, User } from "../../types/api";
import { debounce } from "lodash";
import { getS3SignedUrl } from "../../utils/s3Client";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import { XIcon } from "lucide-react";
import { useTrainingModule } from "./TrainingModuleContext";
import { Dropdown } from "../../components/dropdown/Dropdown";

// Update User interface to reflect role structure
interface CustomUser extends User {
  role?: string | { $oid: string };
}

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

type PastEvaluationRow = {
  id?: string;
  topic: string;
  category: string;
  date: string;
  rawDate: string;
  totalTrainees: number;
  files: string[];
  evaluation: string;
  preTestScore: string;
  postTestScore: string;
  attempted: boolean;
  tests: Test[];
  trainee?: Trainee;
  selectedTestCategory?: "Pre-Test" | "Post-Test";
};

const PastEvaluation: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null); // Properly typed ref
  const [rowData, setRowData] = useState<PastEvaluationRow[]>([]); // Fixed variable name
  const [searchQuery, setSearchQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState("");
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { trainings, fetchTrainings, loading, error } = useTrainingStore();
  const { fetchPremises } = usePremisesStore();
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPremises, setSelectedPremises] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [filterStartDate, filterEndDate] = dateRange;
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  const [filePreview, setFilePreview] = useState<{ url: string; name: string }>(
    {
      url: "",
      name: "",
    },
  );

  const { user } = useAuth() as { user: CustomUser };
  const location = useLocation();

  const userRoles = user?.roles || [];
  const effectiveRole = userRoles.includes("orgadmin")
    ? "orgadmin"
    : userRoles.includes("orguser")
      ? "orguser"
      : "trainee";
  const isTrainer =
    effectiveRole === "orgadmin" ||
    (effectiveRole === "orguser" && viewMode === "trainer");

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Region/premises/date-range/past-bucket/role scoping is now all done
  // server-side (TrainingController.js's `all` action) instead of
  // fetching everything and re-filtering client-side.
  useEffect(() => {
    fetchTrainings({
      type: "past",
      where: isFixedCategory
        ? { moduleType, category: validCategories[0] }
        : { moduleType },
      region: selectedRegion || undefined,
      premises: selectedPremises || undefined,
      roleFilter:
        effectiveRole === "orguser"
          ? viewMode === "trainer"
            ? "trainer"
            : "trainee"
          : undefined,
    });
  }, [
    fetchTrainings,
    moduleType,
    isFixedCategory,
    validCategories,
    selectedRegion,
    selectedPremises,
    filterStartDate,
    filterEndDate,
    effectiveRole,
    viewMode,
  ]);

  useEffect(() => {
    // `trainings` from the store is already scoped — just reshape it into
    // the row shape this screen renders. displayedTestCategory is the
    // canonical server-computed rule instead of the "first Pre-Test found"
    // variant this file used to derive on its own.
    const evaluationData: PastEvaluationRow[] = (
      Array.isArray(trainings) ? trainings : []
    ).map((training) => {
      const tests: Test[] = training.tests || [];
      const trainee = training.trainees?.find(
        (t: Trainee) => t.traineeId === user?.id,
      );
      const isInstructor = training.instructor?.some(
        (inst) => inst.trainerId === user?.id,
      );

      return {
        id: training.id,
        topic: training.topic || "Unknown",
        category: training.category || "Unknown",
        date: formatDate(training.date),
        rawDate: training.date,
        totalTrainees: training.trainees?.length || 0,
        files: training.files || [],
        evaluation: tests.length > 0 ? "Completed" : "Not Started",
        preTestScore: "-",
        postTestScore: "-",
        attempted: false,
        tests,
        trainee: isTrainer && !isInstructor ? undefined : trainee,
        selectedTestCategory: training.displayedTestCategory || "Pre-Test",
      };
    });

    setRowData(evaluationData);

    setTimeout(() => {
      if (gridRef.current?.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    }, 0);
  }, [trainings, user?.id, isTrainer]);

  useEffect(() => {
    if (user?.role !== "orguser") return;
    const saved = localStorage.getItem("training_view_mode");
    if (!saved) {
      localStorage.setItem("training_view_mode", "trainee");
      setViewMode("trainee");
      return;
    }
    if (saved === "trainer" || saved === "trainee") {
      setViewMode(saved);
    }
  }, []);

  const handleViewModeChange = (mode: "trainer" | "trainee") => {
    localStorage.setItem("training_view_mode", mode);
    setViewMode(mode);
  };

  const handleSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setSearchQuery(keyword);
      }, 300),
    [],
  );

  const filteredRowData = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return rowData.filter((evaluation) => {
      // Search filter
      const matchesSearch =
        !searchQuery ||
        evaluation.topic?.toLowerCase().includes(query) ||
        evaluation.category?.toLowerCase().includes(query);

      // Date filter
      let matchesDate = true;

      if (filterStartDate || filterEndDate) {
        const evaluationDate = new Date(evaluation.rawDate);

        if (isNaN(evaluationDate.getTime())) {
          return false;
        }

        if (filterStartDate) {
          const start = new Date(filterStartDate);
          start.setHours(0, 0, 0, 0);

          if (evaluationDate < start) {
            matchesDate = false;
          }
        }

        if (filterEndDate) {
          const end = new Date(filterEndDate);
          end.setHours(23, 59, 59, 999);

          if (evaluationDate > end) {
            matchesDate = false;
          }
        }
      }

      return matchesSearch && matchesDate;
    });
  }, [rowData, searchQuery, filterStartDate, filterEndDate]);

  const handleViewFile = async (fileUrl: string) => {
    try {
      const key = fileUrl.replace(
        "https://dev-kc-repo.s3.ap-south-1.amazonaws.com/",
        "",
      );

      // Get extension to determine MIME type
      const ext = key.split(".").pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        txt: "text/plain",
        jpg: "image/jpeg",
        png: "image/png",
      };
      const mime = mimeTypes[ext || ""] || "application/octet-stream";

      const signed = await getS3SignedUrl(key, mime);
      setFilePreview({ url: signed, name: key.split("/").pop() || "document" });
    } catch (error) {
      console.error("Error viewing file:", error);
    }
  };
  const handleCloseFile = () => {
    setFilePreview({ url: "", name: "" });
  };

  const statusRenderer = (params: ICellRendererParams) => {
    const rowData = params.data as PastEvaluationRow;
    if (isTrainer || !rowData.trainee) return "-";

    const selectedTestCategory = rowData.selectedTestCategory || "Pre-Test";
    const score =
      selectedTestCategory === "Pre-Test"
        ? rowData.trainee.results?.preTestScore
        : rowData.trainee.results?.postTestScore;

    return score != null ? "Attempted" : "Not Attempted";
  };

  const trainerColumnDefs: ColDef[] = [
    { headerName: "Topic", field: "topic", flex: 1, sortable: true },
    { headerName: "Category", field: "category", flex: 1, sortable: true },
    { headerName: "Date", field: "date", flex: 1, sortable: true },
    {
      headerName: "Total Trainees",
      field: "totalTrainees",
      flex: 1,
      sortable: true,
    },
    {
      headerName: "Action",
      field: "action",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => (
        <div>
          <Link to={paths.pastEvaluationDetails(params.data.id)}>
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </Link>
        </div>
      ),
    },
  ];

  const traineeColumnDefs: ColDef[] = [
    { headerName: "Topic", field: "topic", flex: 1, sortable: true },
    {
      headerName: "Category",
      field: "category",
      flex: 1,
      sortable: true,
    },
    { headerName: "Date", field: "date", flex: 1, sortable: true },
    {
      headerName: "Material",
      field: "files",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        const files: string[] = params.data.files;

        if (!files || files.length === 0) {
          return <span>No files</span>;
        }

        return (
          <button
            onClick={() => handleViewFile(files[0])}
            className="text-[#0fa9a6] font-semibold underline"
          >
            View
          </button>
        );
      },
    },
    {
      headerName: "Pre-Test Score",
      field: "preTestScore",
      flex: 1,
      sortable: true,
    },
    {
      headerName: "Post-Test Score",
      field: "postTestScore",
      flex: 1,
      sortable: true,
    },
    {
      headerName: "Status",
      field: "attempted",
      flex: 1,
      cellRenderer: statusRenderer,
    },
  ];

  if (loading && trainings.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  if (error || errorMessage) {
    return (
      <div className="text-red-500 text-center py-8">
        Error: {error || errorMessage}
      </div>
    );
  }

  return (
    <div className="px-2 py-4 mt-14">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Past Evaluations</h2>
        </div>
        <div className="flex items-center gap-4">
          {effectiveRole === "orguser" && (
            <select
              value={viewMode}
              onChange={(e) =>
                handleViewModeChange(e.target.value as "trainee" | "trainer")
              }
              className="border-2 border-[#a5b7cf] p-2 rounded"
            >
              <option value="trainee">Trainee View</option>
              <option value="trainer">Trainer View</option>
            </select>
          )}
          <Link to={paths.dashboard}>
            <button
              type="button"
              className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> BACK
            </button>
          </Link>
        </div>
      </div>

      <div className="flex flex-nowrap items-center gap-3 mb-4 overflow-visible relative z-[9999]">
        <div className="relative flex-1 min-w-[180px] max-w-md flex-shrink">
          <Search className="w-5 h-5 text-[#61748e] absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />

          <input
            type="text"
            placeholder="Search"
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full bg-white border-2 border-[#a5b7cf] rounded-lg pl-10 pr-4 py-2 text-[#5a687c] placeholder:text-[#666768] outline-none focus:outline-none"
          />
        </div>

        <div className="flex-shrink-0">
          <Dropdown
            selectedValue={selectedRegion}
            onChange={(value) => {
              setSelectedRegion(value);
              setSelectedPremises("");
            }}
            type="region"
          />
        </div>

        <div className="flex-shrink-0">
          <Dropdown
            selectedValue={selectedPremises}
            onChange={setSelectedPremises}
            type="premises"
            selectedRegion={selectedRegion}
          />
        </div>

        <div className="flex-shrink-0 relative z-[9999]">
          <DateSelector
            hideToday={true}
            onDateChange={(start, end) => setDateRange([start, end])}
          />
        </div>
      </div>

      <div className="bg-white rounded shadow-md">
        {loading ? (
          <div className="flex justify-center items-center h-[470px]">
            <SpinLoader />
          </div>
        ) : (
          <div className="ag-theme-alpine w-full h-[470px]">
            <AgGridReact
              ref={gridRef}
              rowData={filteredRowData}
              columnDefs={isTrainer ? trainerColumnDefs : traineeColumnDefs}
              rowHeight={50}
              headerHeight={65}
              pagination={true}
              paginationPageSize={20}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={() => ({
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
              })}
            />
          </div>
        )}
        {filePreview.url !== "" && (
          <div
            className="absmiddle"
            style={{
              width: "100vw",
              height: "100vh",
              zIndex: 999999,
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          >
            <XIcon
              className="absolute right-4 top-2 z-10 cursor-pointer bg-red-500 text-white"
              style={{ right: "115px" }}
              onClick={handleCloseFile}
            />
            <div
              className="absmiddle bg-white border py-9"
              style={{
                width: "calc(100vw - 200px)",
                height: "100vh",
                overflow: "auto",
                boxSizing: "border-box",
              }}
            >
              <DocumentViewer file={filePreview.url} name={filePreview.name} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastEvaluation;
